import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const today = () => new Date().toISOString().slice(0, 10);

const FIELD_LABELS = {
  classification: 'Classification', safety_deficiency: 'Safety deficiency', factual_information: 'Factual information',
  probable_cause_link: 'Link to probable cause', recommended_action: 'Recommended action', expected_outcome: 'Expected outcome',
  priority: 'Priority', recommendation: 'Recommendation text',
};

function Shell({ open, onClose, title, description, children, onSubmit, submitLabel, busy, testId }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4" data-testid={testId}>
          {children}
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg">{busy ? 'Working...' : submitLabel}</Button>
            <Button type="button" variant="outline" className="rounded-lg" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Pick({ value, onChange, options, placeholder }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="rounded-lg mt-1"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function RequestResponseDialog({ rec, onClose, onSubmit, busy }) {
  const [form, setForm] = useState({ message: '', due_date: '', send_email: true });
  useEffect(() => { if (rec) setForm({ message: '', due_date: rec.response_deadline || '', send_email: !!rec.addressee_email }); }, [rec]);
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="request-response-form"
      title="Request a response" submitLabel={form.send_email ? 'Send request' : 'Record request'}
      description="Annex 13 asks the addressee to reply within 90 days of the transmittal. Each request starts a new response round."
      onSubmit={() => onSubmit(form)}>
      <div>
        <Label>Response due by</Label>
        <Input type="date" className="rounded-lg mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
      </div>
      <div>
        <Label>Message (optional)</Label>
        <Textarea rows={4} className="rounded-lg mt-1" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Please inform us of the preventive action taken or considered..." />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox checked={form.send_email} disabled={!rec?.addressee_email} onCheckedChange={(v) => setForm({ ...form, send_email: !!v })} className="mt-0.5" />
        <span>
          Email the recommendation to {rec?.addressee_email || 'the addressee'}
          {!rec?.addressee_email && <span className="block text-xs text-slate-500">No email address on file. Untick to record a request sent by letter.</span>}
        </span>
      </label>
    </Shell>
  );
}

export function RecordResponseDialog({ rec, options, onClose, onSubmit, busy }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (rec) setForm({ received_at: today(), responder: '', response_type: '', response_text: '', assessment: '', assessment_comment: '' }); }, [rec]);
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="record-response-form"
      title="Record the addressee's response" submitLabel="Save response" onSubmit={() => onSubmit(form)}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Received on</Label><Input type="date" className="rounded-lg mt-1" value={form.received_at || ''} onChange={(e) => setForm({ ...form, received_at: e.target.value })} /></div>
        <div><Label>From</Label><Input className="rounded-lg mt-1" value={form.responder || ''} onChange={(e) => setForm({ ...form, responder: e.target.value })} placeholder="Name / office" /></div>
      </div>
      <div><Label>Type of response *</Label><Pick value={form.response_type} onChange={(v) => setForm({ ...form, response_type: v })} options={options.response_types} placeholder="Select" /></div>
      <div><Label>Response *</Label><Textarea rows={5} className="rounded-lg mt-1" value={form.response_text || ''} onChange={(e) => setForm({ ...form, response_text: e.target.value })} required /></div>
      <div><Label>Your assessment</Label><Pick value={form.assessment} onChange={(v) => setForm({ ...form, assessment: v })} options={options.assessments} placeholder="Assess later" /></div>
      {form.assessment && (
        <div><Label>Assessment comment</Label><Textarea rows={2} className="rounded-lg mt-1" value={form.assessment_comment || ''} onChange={(e) => setForm({ ...form, assessment_comment: e.target.value })} /></div>
      )}
    </Shell>
  );
}

export function DuplicateDialog({ rec, options, onClose, onSubmit, busy }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (rec) setForm({ addressee: '', addressee_organisation: '', addressee_email: '', addressee_state: '', fields: options.copyable_fields }); }, [rec, options.copyable_fields]);
  const toggle = (f) => setForm((s) => ({ ...s, fields: s.fields.includes(f) ? s.fields.filter((x) => x !== f) : [...s.fields, f] }));
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="duplicate-form"
      title="Issue to another addressee" submitLabel="Create draft"
      description="Each recommendation has one addressee. This creates a new draft with its own number, copying the fields you tick."
      onSubmit={() => onSubmit(form)}>
      <div><Label>Addressee *</Label><Input className="rounded-lg mt-1" value={form.addressee || ''} onChange={(e) => setForm({ ...form, addressee: e.target.value })} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Organisation</Label><Input className="rounded-lg mt-1" value={form.addressee_organisation || ''} onChange={(e) => setForm({ ...form, addressee_organisation: e.target.value })} /></div>
        <div><Label>State</Label><Input className="rounded-lg mt-1" value={form.addressee_state || ''} onChange={(e) => setForm({ ...form, addressee_state: e.target.value })} /></div>
      </div>
      <div><Label>Email</Label><Input type="email" className="rounded-lg mt-1" value={form.addressee_email || ''} onChange={(e) => setForm({ ...form, addressee_email: e.target.value })} /></div>
      <div>
        <Label>Copy these fields</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {(options.copyable_fields || []).map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.fields?.includes(f)} onCheckedChange={() => toggle(f)} />{FIELD_LABELS[f] || f}
            </label>
          ))}
        </div>
      </div>
    </Shell>
  );
}

export function CloseDialog({ rec, options, onClose, onSubmit, busy }) {
  const [form, setForm] = useState({ reason: '', final_assessment: '' });
  useEffect(() => { if (rec) setForm({ reason: '', final_assessment: rec.latest_assessment || '' }); }, [rec]);
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="close-form" title="Close recommendation" submitLabel="Close recommendation"
      onSubmit={() => onSubmit(form)}>
      <div><Label>Final assessment</Label><Pick value={form.final_assessment} onChange={(v) => setForm({ ...form, final_assessment: v })} options={options.assessments} placeholder="Select" /></div>
      <div><Label>Reason for closing *</Label><Textarea rows={4} className="rounded-lg mt-1" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required /></div>
    </Shell>
  );
}

export function NoteDialog({ rec, onClose, onSubmit, busy }) {
  const [form, setForm] = useState({ subject: '', text: '' });
  useEffect(() => { if (rec) setForm({ subject: '', text: '' }); }, [rec]);
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="note-form" title="Add internal note" submitLabel="Add note"
      description="Notes are visible to administrators and IICs only and are never included in exports."
      onSubmit={() => onSubmit(form)}>
      <div><Label>Subject</Label><Input className="rounded-lg mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
      <div><Label>Note *</Label><Textarea rows={5} className="rounded-lg mt-1" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required /></div>
    </Shell>
  );
}

export function LinkDialog({ rec, investigations, onClose, onSubmit, busy }) {
  const [invId, setInvId] = useState('');
  useEffect(() => { if (rec) setInvId(''); }, [rec]);
  const available = investigations.filter((i) => !(rec?.linked_investigation_ids || []).includes(i.id));
  return (
    <Shell open={!!rec} onClose={onClose} busy={busy} testId="link-form" title="Link another investigation" submitLabel="Link"
      description="Use this when the same safety deficiency was found in more than one investigation."
      onSubmit={() => invId && onSubmit({ investigation_id: invId })}>
      <Pick value={invId} onChange={setInvId} placeholder="Select investigation"
        options={available.map((i) => ({ value: i.id, label: `${i.investigation_number} - ${i.title || ''}` }))} />
    </Shell>
  );
}
